// ─────────────────────────────────────────────────────────────────────────────
// Encuadre inicial de los mapas operativos, derivado de dónde opera el cliente.
//
// POR QUÉ EXISTE: los mapas de la app arrancaban con el centro de Santiago
// hardcodeado, así que un operador de Valparaíso, Mendoza o Buenos Aires abría
// /admin/live-tracking sobre una ciudad que no es la suya. En Seguimiento en vivo
// el efecto no era solo estético: `StopClusterLayer` filtra las paradas por el
// viewport actual, así que fuera del encuadre inicial no se renderizaba NINGUNA
// parada hasta panear a mano.
//
// La fuente es lo que el propio cliente declaró en /admin/settings?tab=locations:
//   · Operadores logísticos → `logisticCompany.operationsAddresses[]`, con `lat`
//     y `lng` planos. El formulario obliga a geocodificar antes de guardar, así
//     que las direcciones nuevas siempre las tienen; las legadas pueden no.
//   · Comercios con flota propia → `company.shippingAddresses[]`, que anidan las
//     coordenadas en `geo` y traen `geocodingStatus`. El schema Company NO tiene
//     `operationsAddresses`, y estas rutas montan los MISMOS componentes bajo
//     /home/*, por eso el helper acepta las dos formas.
//
// Este módulo es puro: sin React, sin fetch, sin Leaflet. Devuelve datos que las
// vistas aplican imperativamente (el `center` de <MapContainer> es inerte tras el
// montaje; ver lib/react-leaflet-lite.jsx).
// ─────────────────────────────────────────────────────────────────────────────

import { addressLatLng, geocodeDefaultsFor } from './geocode';

/** Zoom cuando el operador declaró una sola instalación: comuna/barrio, no calle. */
const SINGLE_POINT_ZOOM = 12;

/** Zoom del respaldo por país, heredado del que ya usaban los mapas. */
const COUNTRY_ZOOM = 11;

// Zoom aproximado para que un bbox entre en un viewport típico de escritorio.
// Es una heurística deliberada: sirve a las vistas que solo pueden fijar
// `center`/`zoom` al crear el mapa y no pueden hacer `fitBounds`. Las que sí
// pueden deben usar `bounds`, que es exacto.
const ZOOM_BY_SPREAD = [
  { maxDegrees: 0.15, zoom: 12 }, // una ciudad
  { maxDegrees: 0.5, zoom: 11 }, // área metropolitana
  { maxDegrees: 1.5, zoom: 9 }, // una región
  { maxDegrees: 5, zoom: 7 }, // varias regiones
];
const WIDE_SPREAD_ZOOM = 5; // país entero o más

/**
 * Estados de geocodificación utilizables de `shippingAddresses[]`. `pending` y
 * `failed` significan que las coordenadas no se resolvieron: encuadrar sobre
 * ellas sería peor que caer al centro del país. Las direcciones que no declaran
 * el campo (operaciones legadas y `operationsAddresses`) se aceptan: ahí la
 * ausencia no significa "falló", significa "este schema no lo tiene".
 */
const USABLE_GEOCODING_STATUS = new Set(['ok', 'manual']);

/**
 * Normaliza una dirección de cualquiera de las dos formas a `[lat, lng]`.
 *
 * Se apoya en `addressLatLng`, que rechaza `null`/`''` ANTES de coercer — sin
 * eso, `Number(null) === 0` convierte un formulario vacío en el punto (0,0) del
 * Golfo de Guinea, que es una coordenada perfectamente válida y estira cualquier
 * bbox a medio planeta.
 *
 * @param {object} addr dirección operativa o de envío
 * @returns {[number, number] | null}
 */
export function addressPoint(addr) {
  if (!addr) return null;

  // `shippingAddresses[]` (comercio): coordenadas en `geo`, con estado explícito.
  if (addr.geo && typeof addr.geo === 'object') {
    const status = addr.geocodingStatus;
    if (status != null && !USABLE_GEOCODING_STATUS.has(status)) return null;
    return inRange(addressLatLng(addr.geo));
  }

  // `operationsAddresses[]` (logística): coordenadas planas.
  return inRange(addressLatLng(addr));
}

/**
 * Descarta coordenadas fuera del rango geográfico válido. El DTO del backend ya
 * las acota, pero los datos legados se escribieron antes de esa validación y un
 * par invertido (lng en lat) puede colarse con ambos valores finitos.
 */
function inRange(point) {
  if (!point) return null;
  const [lat, lng] = point;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return point;
}

/** Zoom para un bbox, según cuántos grados abarca su lado mayor. */
function zoomForSpread(spread) {
  const match = ZOOM_BY_SPREAD.find((entry) => spread < entry.maxDegrees);
  return match ? match.zoom : WIDE_SPREAD_ZOOM;
}

/**
 * Encuadre inicial de un mapa operativo.
 *
 * Cascada: instalaciones declaradas por el cliente → centro de su país. Nunca
 * devuelve `null` ni lanza: un mapa sin encuadre no es un estado que las vistas
 * puedan renderizar, y `fitBounds` con bounds inválidos lanza dentro de un
 * `useEffect` (lo que en esta app termina en el ErrorBoundary global y una
 * pantalla en blanco). Si no hay nada que usar, el respaldo por país siempre
 * responde, porque `geocodeDefaultsFor` cae a Chile ante un país desconocido.
 *
 * Devuelve `bounds` Y `center`/`zoom` a la vez a propósito: las vistas que montan
 * un controlador imperativo usan `bounds` (exacto); las que solo necesitan
 * arreglar el arranque del `<MapContainer>` usan `center`/`zoom` sin montar nada.
 *
 * LIMITACIÓN CONOCIDA: se encuadran TODAS las instalaciones, sin filtrar por tipo
 * ni por país. Un operador con bodega en Santiago y otra en Mendoza obtiene un
 * bbox que abarca las dos, y por lo tanto un zoom bajo. Es la decisión de producto
 * vigente; el `zoom` derivado del spread la hace legible, no la corrige.
 *
 * @param {Array<object>} addresses `operationsAddresses[]` o `shippingAddresses[]`
 * @param {{countryCode?: string}} [ctx] país de operación (ISO 3166-1 alpha-2)
 * @returns {{
 *   bounds: [[number, number], [number, number]] | null,
 *   center: [number, number],
 *   zoom: number,
 *   source: 'addresses' | 'country'
 * }}
 */
export function resolveOperationFraming(addresses, { countryCode } = {}) {
  const points = (Array.isArray(addresses) ? addresses : [])
    .map(addressPoint)
    .filter(Boolean);

  if (points.length === 0) return countryFraming(countryCode);

  if (points.length === 1) {
    return {
      bounds: null,
      center: points[0],
      zoom: SINGLE_POINT_ZOOM,
      source: 'addresses',
    };
  }

  const lats = points.map((p) => p[0]);
  const lngs = points.map((p) => p[1]);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);

  // Varias instalaciones en la MISMA coordenada (duplicados, o un edificio con
  // oficina y bodega) producen un bbox de área cero, que Leaflet acepta pero
  // encuadra con un zoom máximo absurdo. Se trata como punto único.
  if (minLat === maxLat && minLng === maxLng) {
    return {
      bounds: null,
      center: [minLat, minLng],
      zoom: SINGLE_POINT_ZOOM,
      source: 'addresses',
    };
  }

  const spread = Math.max(maxLat - minLat, maxLng - minLng);
  return {
    bounds: [
      [minLat, minLng],
      [maxLat, maxLng],
    ],
    center: [(minLat + maxLat) / 2, (minLng + maxLng) / 2],
    zoom: zoomForSpread(spread),
    source: 'addresses',
  };
}

/** Respaldo por país. `geocodeDefaultsFor` ya cae a Chile ante un país desconocido. */
function countryFraming(countryCode) {
  const { center } = geocodeDefaultsFor(countryCode);
  return {
    bounds: null,
    center: [center.lat, center.lng],
    zoom: COUNTRY_ZOOM,
    source: 'country',
  };
}

/**
 * Instalaciones declaradas por el usuario autenticado, en la forma que espera
 * `resolveOperationFraming`. Vive acá y no en el hook para poder testearla sin
 * montar React.
 *
 * Un usuario de operador logístico tiene `logisticCompany`; un comercio con flota
 * propia solo tiene `company`, cuyo claim NO incluye `operationsAddresses` (el
 * schema Company no lo tiene) pero sí `shippingAddresses`.
 *
 * @param {object} user usuario decodificado del JWT (`useAuth().user`)
 * @returns {Array<object>}
 */
export function operationAddressesOf(user) {
  const operations = user?.logisticCompany?.operationsAddresses;
  if (Array.isArray(operations) && operations.length > 0) return operations;

  const shipping = user?.company?.shippingAddresses;
  if (Array.isArray(shipping) && shipping.length > 0) return shipping;

  return [];
}
